import { zodResolver } from "@hookform/resolvers/zod";
import { LoaderCircle, SquarePlus } from "lucide-react";

import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { useAddQuestion } from "@/api/chat-settings/add-predefined-question";
import { useGetQuestion } from "@/api/chat-settings/get-predefined-question";
import { useUpdateQuestion } from "@/api/chat-settings/update-predefined-question";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import useUpdateQueryParams from "@/hooks/use-update-query-params";
import { useSearchParams } from "react-router";
import useGlobalPermission from "@/store/user-permissions";

const formSchema = z.object({
    question: z
        .string({
            required_error: "Question is Required",
            invalid_type_error: "Question must be a string",
        })
        .min(1, "Question cannot be empty"),
    answer: z
        .string({
            required_error: "Answer is Required",
            invalid_type_error: "Answer must be a string",
        })
        .min(1, "Answer cannot be empty"),
    is_active: z.boolean(),
});

type QuestionType = {
    id: string;
    question: string;
    answer: string;
    is_active: boolean;
};

type Props = {
    question?: QuestionType;
};

const PredefinedQuestionForm: React.FC<Props> = () => {
    const [open, setOpen] = React.useState<boolean>(false);

    const updateSearchParams = useUpdateQueryParams();
    const [searchParams] = useSearchParams();

    const { data, isLoading: questionLoading } = useGetQuestion({
        id: searchParams.get("edit_question_id") || "",
    });

    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            question: "",
            answer: "",
            is_active: false,
        },
    });

    const { mutate: addQuestion, isSuccess, isPending } = useAddQuestion();
    const { mutate: updateQuestion, isSuccess: updateSuccess, isPending: updatePending } = useUpdateQuestion();

    function handlePredefinedQuestions(values: z.infer<typeof formSchema>) {
        if (searchParams.get("edit_question_id")) {
            updateQuestion({ id: data.data.id, payload: values });
        }

        if (!searchParams.get("edit_question_id")) {
            addQuestion(values);
        }
    }

    React.useEffect(() => {
        if (isSuccess || updateSuccess) {
            setOpen(false);
            form.reset();
        }
    }, [isSuccess, form, updateSuccess]);

    useEffect(() => {
        if (searchParams.get("edit_question_id")) {
            setOpen(true);
        }
    }, [searchParams]);

    useEffect(() => {
        if (data) {
            form.reset({
                question: data.data.question,
                answer: data.data.answer,
                is_active: data.data.is_active,
            });
        }
    }, [data, form]);

    const { permissionList } = useGlobalPermission();
    const faqPermission = permissionList.find((item) => item.module.name === "Questions");

    return (
        <>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                    <Button disabled={!faqPermission?.can_create}>
                        <SquarePlus /> Add New Question
                    </Button>
                </DialogTrigger>
                <DialogContent
                    className="sm:max-w-[425px] [&>button]:hidden"
                    onPointerDownOutside={() => {
                        updateSearchParams({ edit_question_id: undefined });
                        form.reset({
                            question: "",
                            answer: "",
                            is_active: false,
                        });
                    }}
                    onEscapeKeyDown={() => {
                        updateSearchParams({ edit_question_id: undefined });
                        form.reset({
                            question: "",
                            answer: "",
                            is_active: false,
                        });
                    }}
                >
                    <DialogHeader>
                        <DialogTitle>{searchParams.get("edit_question_id") ? "Edit" : "Add New"} Predefined Question</DialogTitle>
                        <DialogDescription>
                            Predefined questions will show up as FAQs in the chat widget that users can browse to get an instant answer.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                        <div className="w-full rounded-lg bg-white">
                            <Form {...form}>
                                <form onSubmit={form.handleSubmit(handlePredefinedQuestions)}>
                                    <FormField
                                        control={form.control}
                                        name="question"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel className="text-base">Question*</FormLabel>
                                                <FormControl>
                                                    {questionLoading ? (
                                                        <Skeleton className="h-20 w-full rounded-md bg-gray-200" />
                                                    ) : (
                                                        <Textarea className="py-6" placeholder="Enter Question" {...field} />
                                                    )}
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                    <FormField
                                        control={form.control}
                                        name="answer"
                                        render={({ field }) => (
                                            <FormItem className="my-4">
                                                <FormLabel className="text-base">Answer*</FormLabel>
                                                <FormControl>
                                                    {questionLoading ? (
                                                        <Skeleton className="h-20 w-full rounded-md bg-gray-200" />
                                                    ) : (
                                                        <Textarea className="py-6" placeholder="Enter Answer" {...field} />
                                                    )}
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                    <FormField
                                        control={form.control}
                                        name="is_active"
                                        render={({ field }) => (
                                            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm">
                                                <div className="space-y-0.5">
                                                    <FormLabel>Question Active</FormLabel>
                                                    <FormDescription>Should this question be shown in the chat widget?</FormDescription>
                                                </div>
                                                <FormControl>
                                                    {questionLoading ? (
                                                        <Skeleton className="h-20 w-full rounded-md bg-gray-200" />
                                                    ) : (
                                                        <Switch checked={field.value} onCheckedChange={field.onChange} aria-readonly />
                                                    )}
                                                </FormControl>
                                            </FormItem>
                                        )}
                                    />
                                    <div className="col-span-2 mt-4 flex justify-end">
                                        <Button type="submit" disabled={isPending || updatePending}>
                                            {isPending || (updatePending && <LoaderCircle className="animate-spin" />)}
                                            {searchParams.get("edit_question_id") ? "Updat" : "Sav"}
                                            {isPending || updatePending ? "ing..." : "e"}
                                        </Button>
                                    </div>
                                </form>
                            </Form>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default PredefinedQuestionForm;
